import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import EntityLink from "../components/EntityLink";
import { useAuth } from "../contexts/AuthContext";
import { api, API_BASE, formatApiError } from "../lib/api";
import { useHasPermission } from "../contexts/PermissionsContext";
import { formatDate, formatDateTime, statusBadgeClass } from "../lib/domain";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import useFilterUrlSync from "../hooks/useFilterUrlSync";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { ArrowLeft, Download, FileSpreadsheet, FileText, Layers, Printer, RefreshCw, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import PayrollLineBreakdown from "../components/PayrollLineBreakdown";
import DeductionBreakdown from "../components/DeductionBreakdown";
import { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import ModuleIdChip from "../components/ModuleIdChip";
import WorkerTypeBadge, { workerTypeLabel } from "../components/WorkerTypeBadge";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STEPS = ["draft", "prepared", "dept_confirmed", "finance_reviewed", "approved", "funded", "paid", "closed"];

// Iter60.p51 — Worker-type taxonomy → readable badge.
// The wage_rate catalog stores `casual | non_casual_manual | non_casual_non_manual`.
// The Line schema surfaces `worker_category` (same string) so the badge
// renders directly off the payroll line.
// Iter60.p58 — implementation moved to /components/WorkerTypeBadge.jsx so
// the Attendance grid can share the same taxonomy.

// Iter66 — Wage rate cell renderer for the Payroll grid.
// `wage_rate` + `wage_rate_unit` are hydrated by the backend from
// `payroll_lines.wage_rate_snapshot`. Unit → display suffix mapping
// mirrors `_ITER66_UNIT_SUFFIX` on the server so the grid and the
// XLSX / PDF exports render identically.
const _ITER66_UNIT_SUFFIX = {
    hour: "/hr",
    day: "/day",
    week: "/wk",
    biweek: "/2wk",
    month: "/mo",
};

function formatWageRate(rate, unit) {
    if (rate === null || rate === undefined) return "—";
    const n = Number(rate);
    if (!Number.isFinite(n)) return "—";
    const suffix = _ITER66_UNIT_SUFFIX[(unit || "").toLowerCase()] || "";
    // Integer amounts render without decimals for readability.
    const body = Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
    return `${body}${suffix}`;
}

export default function PayrollPeriod() {
    const { id, moduleCode: pathModuleCode } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [period, setPeriod] = useState(null);
    const [lines, setLines] = useState([]);
    // Iter74.7 — Track the FULL period line count (unfiltered) separately
    // so the tab counter can render `Lines (87 / 125)` when a server-side
    // filter narrows the visible set. Sourced from
    // `period.worker_count` (stamped by compute_period_totals — always
    // reflects the complete line set on the period).
    const [linesTotal, setLinesTotal] = useState(0);
    const [funding, setFunding] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fundsDrawerOpen, setFundsDrawerOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    // Iter60.p43.5 — Search input (name / employee_code / Module ID).
    // Client-side filter — `load()` already fetches the whole period
    // page (page_size=500) so debounce guards keystroke thrash, not
    // request thrash.
    const [searchRaw, setSearchRaw] = useState("");
    const [search, setSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setSearch(searchRaw.trim().toLowerCase()), 250);
        return () => clearTimeout(t);
    }, [searchRaw]);

    // Iter74 — Bank Account triage chip on the Payroll Lines table.
    // `all | has | none`. Sent as a query param so `total` reflects only
    // matching rows and the backend can compose with existing filters.
    const [bankAccount, setBankAccount] = useState("all");
    // Iter74.7 — Amount-to-pay triage chip. `all | has | zero`. Display
    // filter only — period totals, funding, exports, warnings are all
    // computed against the full line set (compliance-critical).
    const [amountToPay, setAmountToPay] = useState("all");
    useFilterUrlSync({
        filters: { bank_account: bankAccount, amount_to_pay: amountToPay },
        defaults: { bank_account: "all", amount_to_pay: "all" },
        paramMap: { bank_account: "bank_account", amount_to_pay: "amount_to_pay" },
        hydrate: (patch) => {
            if (patch.bank_account !== undefined) setBankAccount(patch.bank_account);
            if (patch.amount_to_pay !== undefined) setAmountToPay(patch.amount_to_pay);
        },
    });

    const modId = period?.module_id || null;
    const canPrepare = useHasPermission("payroll.prepare", modId);
    const canRecompute = useHasPermission("payroll.recompute", modId);
    const canDeptConfirm = useHasPermission("payroll.dept_confirm", modId);
    const canFinanceReview = useHasPermission("payroll.finance_review", modId);
    const canApprove = useHasPermission("payroll.approve", modId);
    const canRequestFunding = useHasPermission("payroll_funding.write", modId);
    const canFundsReceived = useHasPermission("payroll_funding.mark_funds_received", modId);
    const canRelease = useHasPermission("payroll_funding.release", modId);
    const canCancel = useHasPermission("payroll.cancel", modId);
    const canEdit = useHasPermission("payroll_line.write", modId);
    const canMarkPaidPerm = useHasPermission("payroll_line.mark_paid", modId);
    // Iter84 (2026-02) — Super Admin delete guard for approved+ periods.
    // Cancel button hides at approved+ for everyone; the crimson Delete
    // button shows ONLY for kaafi_system_admin at approved / funded /
    // paid / closed. Uses a typed-phrase confirmation modal to make
    // an accidental click impossible.
    const isSuperAdmin = user?.role === "kaafi_system_admin";
    const iter84AdminOnlyStatuses = ["approved", "funded", "paid", "closed"];
    const isApprovedOrLater = iter84AdminOnlyStatuses.includes(period?.status);
    const [iter84DeleteOpen, setIter84DeleteOpen] = useState(false);
    const [iter84DeleteReason, setIter84DeleteReason] = useState("");
    const [iter84DeletePhrase, setIter84DeletePhrase] = useState("");
    const [iter84Deleting, setIter84Deleting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            // Iter74 — Only forward `bank_account` when it's not the "all"
            // sentinel so the URL stays clean and the backend applies no
            // additional predicate.
            const linesParams = { page_size: 500 };
            if (bankAccount !== "all") linesParams.bank_account = bankAccount;
            // Iter74.7 — Same treatment for `amount_to_pay`.
            if (amountToPay !== "all") linesParams.amount_to_pay = amountToPay;
            const [p, l, f] = await Promise.all([
                api.get(`/payroll/periods/${id}`),
                api.get(`/payroll/periods/${id}/lines`, { params: linesParams }),
                api.get(`/payroll/funding`),
            ]);
            setPeriod(p.data);
            setLines(l.data.items || []);
            // Iter74.7 — Anchor the unfiltered total on
            // `period.totals.worker_count` (stamped by
            // compute_period_totals; always reflects the FULL line
            // set). Fall back to `l.data.total` when the period is
            // ephemeral / totals are stale (e.g., first paint on a
            // brand-new period before totals compute).
            setLinesTotal(
                p.data?.totals?.worker_count
                ?? l.data.total
                ?? (l.data.items?.length ?? 0)
            );
            setFunding((f.data || []).find((x) => x.period_id === id) || null);
        } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [id, bankAccount, amountToPay]);

    const act = async (path, payload) => {
        try {
            await api.post(`/payroll/periods/${id}${path}`, payload || {});
            toast.success("Done"); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const fundAct = async (suffix, payload) => {
        if (!funding) return;
        try { await api.post(`/payroll/funding/${funding.id}${suffix}`, payload || {}); toast.success("Done"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    // Iter84 — Super Admin soft-delete for approved+ periods. Requires
    // a typed `DELETE` phrase + a non-empty reason. Sends the reason
    // in the DELETE body so audit + `deleted_reason` are captured.
    const iter84Delete = async () => {
        setIter84Deleting(true);
        try {
            await api.delete(`/payroll/periods/${id}`, {
                data: { reason: iter84DeleteReason.trim() },
            });
            toast.success("Period deleted. Restore from Admin → Payroll Trash.");
            setIter84DeleteOpen(false);
            // After a successful delete we bounce back to the module
            // payroll list so the operator doesn't stare at a
            // now-hidden period.
            const modCode = period?.module_code || null;
            if (modCode) {
                navigate(`/m/${modCode}/payroll`);
            } else {
                load();
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setIter84Deleting(false);
        }
    };

    if (loading || !period) return <AppShell title="Period"><div className="asl-card p-6 text-sm">Loading…</div></AppShell>;

    const t = period.totals || {};

    // Iter60.p43.5 — Client-side search filter across Lines + Warnings.
    // Cost by Project matches on project_name substring (delegated to
    // the tab component). Empty search short-circuits to the raw list
    // so the reference is stable and downstream memos don't recompute.
    //
    // Iter60.p49 Item C — `employee_code` (EMP-####) dropped from the
    // predicate. Typing "348" for Module ID 348G was hitting EMP-4348
    // for a completely different employee. Bag now matches ONLY name
    // + Module ID.
    const filteredLines = !search
        ? lines
        : lines.filter((l) => {
            const bag = [
                l.employee_name,
                l.module_employee_id,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return bag.includes(search);
        });
    const rawWarnings = period.warnings || [];
    // Iter60.p49 Item A — Structured warnings emitted by the backend
    // (`build_lines_for_period`). Preferred over the flat `warnings`
    // strings because it lets us render a per-worker card with a "Fix"
    // CTA (e.g., jump to Wage Rates with prefill query params). Falls
    // back gracefully for periods computed before p49.
    const rawWarningDetails = period.warning_details || [];
    const filteredWarnings = !search
        ? rawWarnings
        : rawWarnings.filter((w) => String(w).toLowerCase().includes(search));
    const filteredWarningDetails = !search
        ? rawWarningDetails
        : rawWarningDetails.filter((d) => {
            const bag = [
                d.employee_name, d.module_employee_id, d.job_role,
                d.worker_category, d.reason, d.kind,
            ].filter(Boolean).join(" ").toLowerCase();
            return bag.includes(search);
        });

    return (
        <AppShell title="Payroll Period">
            <FundsReceivedDrawer
                open={fundsDrawerOpen}
                funding={funding}
                onClose={() => setFundsDrawerOpen(false)}
                onSaved={load}
            />
            <section className="space-y-5" data-testid="payroll-period-page">
                <div className="flex items-center justify-between">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            // History-aware Back: honor the browser stack when the
                            // user arrived from another page (e.g. dashboard, staff),
                            // otherwise fall back to the payroll list.
                            if (window.history.state && window.history.state.idx > 0) {
                                navigate(-1);
                            } else {
                                navigate("/payroll");
                            }
                        }}
                        data-testid="period-back"
                    >
                        <ArrowLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <a href={`${API_BASE}/payroll/periods/${id}/export.csv`} target="_blank" rel="noreferrer">
                        <Button variant="outline" size="sm" data-testid="period-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                    </a>
                </div>

                <div className="asl-card p-5">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-xs uppercase text-[color:var(--asl-muted)]">{period.period_type} · {period.worker_category_scope}</div>
                            <h2 className="text-2xl font-bold asl-navy-text">{period.name}</h2>
                            <div className="mono text-sm asl-crimson-text">{formatDate(period.start_date)} → {formatDate(period.end_date)}</div>
                        </div>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${statusBadgeClass(period.status)}`} data-testid="period-status">{period.status}</span>
                    </div>

                    {/* Stepper */}
                    <div className="flex items-center gap-1 flex-wrap mt-4 text-xs" data-testid="period-stepper">
                        {STEPS.map((s) => {
                            const i = STEPS.indexOf(s);
                            const cur = STEPS.indexOf(period.status);
                            const done = cur >= 0 && i <= cur;
                            return <span key={s} className={`px-2 py-1 rounded-full ${done ? "asl-navy-bg text-white" : "bg-gray-100 text-gray-500"}`}>{s}</span>;
                        })}
                    </div>

                    {/* Totals */}
                    <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mt-5">
                        <Stat label="Workers" value={t.worker_count || 0} />
                        <Stat label="Gross" value={fmtUSD(t.gross_total)} />
                        <Stat label="OT" value={fmtUSD(t.overtime_total)} />
                        <Stat label="Uplift" value={fmtUSD(t.weekend_holiday_uplift)} />
                        <Stat label="Net" value={fmtUSD(t.net_payable_total)} accent />
                        {/* Iter60.p26 — Management Fee KPI. Sourced from
                            the summed per-line management_fee; falls back
                            to the legacy period-level derivation on old
                            periods that predate the per-line field. */}
                        <Stat
                            label="Mgmt Fee"
                            value={fmtUSD(t.management_fee_total || t.management_fee || 0)}
                            testid="kpi-management-fee"
                        />
                        <Stat label="Fund Req" value={fmtUSD(t.funding_required_total)} accent />
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 mt-5" data-testid="period-actions">
                        {canPrepare && ["draft", "prepared"].includes(period.status) &&
                            <Button size="sm" className="asl-navy-bg text-white" onClick={() => act("/prepare")} data-testid="act-prepare">Prepare</Button>}
                        {canRecompute && ["draft", "prepared"].includes(period.status) &&
                            <Button size="sm" variant="outline" onClick={() => act("/recompute")} data-testid="act-recompute"><RefreshCw className="h-3 w-3 mr-1" /> Recompute</Button>}
                        {/* Iter58 Part C — Bulk allowance / deduction on this period */}
                        {canEdit && !["paid", "closed", "cancelled"].includes(period.status) &&
                            <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="act-bulk-line-item">
                                <Layers className="h-3 w-3 mr-1" /> Bulk allowance/deduction
                            </Button>}
                        {canDeptConfirm && period.status === "prepared" &&
                            <Button size="sm" className="asl-navy-bg text-white" onClick={() => act("/dept-confirm")} data-testid="act-dept-confirm">Dept Confirm</Button>}
                        {canFinanceReview && period.status === "dept_confirmed" &&
                            <Button size="sm" className="asl-navy-bg text-white" onClick={() => act("/finance-review")} data-testid="act-finance-review">Finance Review</Button>}
                        {canApprove && period.status === "finance_reviewed" &&
                            <Button size="sm" className="asl-navy-bg text-white" onClick={() => act("/approve")} data-testid="act-approve">Approve</Button>}
                        {canRequestFunding && period.status === "approved" && !funding &&
                            <Button size="sm" className="asl-crimson-bg text-white" onClick={() => act("/funding-request", {})} data-testid="act-funding-request">Request Funding</Button>}
                        {canFundsReceived && funding && funding.status === "requested" &&
                            <Button size="sm" className="asl-navy-bg text-white"
                                onClick={() => setFundsDrawerOpen(true)}
                                data-testid="act-funds-received">Mark Funds Received</Button>}
                        {canRelease && period.status === "funded" && funding &&
                            <Button size="sm" className="asl-navy-bg text-white" onClick={() => fundAct("/release")} data-testid="act-release">Release Payments</Button>}
                        {canCancel && !isApprovedOrLater && !["paid", "closed", "cancelled"].includes(period.status) &&
                            <Button size="sm" variant="outline" onClick={() => act("/cancel")} data-testid="act-cancel">Cancel</Button>}
                        {/* Iter84 (2026-02) — Super Admin delete for
                            approved / funded / paid / closed periods.
                            Non-admins see nothing; a Cancel button is
                            deliberately absent past `approved` so the
                            operator can't confuse cancel-vs-delete. */}
                        {isSuperAdmin && isApprovedOrLater &&
                            <Button
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={() => { setIter84DeleteReason(""); setIter84DeletePhrase(""); setIter84DeleteOpen(true); }}
                                data-testid="iter84-admin-delete-btn"
                            >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Delete (Super Admin)
                            </Button>}
                        <a href={`${API_BASE}/payroll/periods/${period.id}/summary.pdf`} target="_blank" rel="noreferrer" className="ml-auto">
                            <Button size="sm" variant="outline" data-testid="period-export-pdf">
                                <FileText className="h-3 w-3 mr-1" /> Export PDF
                            </Button>
                        </a>
                        <a href={`${API_BASE}/payroll/periods/${period.id}/lines.xlsx`} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline" data-testid="period-export-xlsx">
                                <FileSpreadsheet className="h-3 w-3 mr-1" /> Export Excel
                            </Button>
                        </a>
                        <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="period-print-summary">
                            <Printer className="h-3 w-3 mr-1" /> Print Summary
                        </Button>
                    </div>
                </div>

                <Tabs defaultValue="lines">
                    <TabsList>
                        <TabsTrigger value="lines" data-testid="tab-lines">
                            {/* Iter74.7 — Show `filtered / total` when a
                                server-side filter narrows the visible set,
                                so the user always sees the underlying
                                unfiltered count (period.worker_count). */}
                            Lines ({filteredLines.length}
                            {(bankAccount !== "all" || amountToPay !== "all" || search) && linesTotal > filteredLines.length
                                ? ` / ${linesTotal}`
                                : ""})
                        </TabsTrigger>
                        <TabsTrigger value="cost-by-project" data-testid="tab-cost-by-project">Cost by Project</TabsTrigger>
                        <TabsTrigger value="warnings" data-testid="tab-warnings">Warnings ({filteredWarnings.length}{search ? ` / ${rawWarnings.length}` : ""})</TabsTrigger>
                        <TabsTrigger value="funding" data-testid="tab-funding">Funding</TabsTrigger>
                        <TabsTrigger value="audit" data-testid="tab-period-audit">Audit</TabsTrigger>
                    </TabsList>
                    {/* Iter60.p43.5 — Shared search input above the tab
                        content. Filters Lines by name/employee_code/
                        Module ID, Warnings by substring, and Cost by
                        Project by project_name substring. */}
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 max-w-md">
                            <Search
                                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
                                aria-hidden="true"
                                style={{ color: "var(--asl-text-3)" }}
                            />
                            <Input
                                type="text"
                                value={searchRaw}
                                onChange={(e) => setSearchRaw(e.target.value)}
                                placeholder="Search name or module ID…"
                                className="pl-9 pr-9"
                                data-testid="payroll-period-search"
                                aria-label="Search payroll lines"
                            />
                            {searchRaw && (
                                <button
                                    type="button"
                                    onClick={() => setSearchRaw("")}
                                    aria-label="Clear search"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-black/[.05]"
                                    data-testid="payroll-period-search-clear"
                                >
                                    <X
                                        className="h-3.5 w-3.5"
                                        aria-hidden="true"
                                        style={{ color: "var(--asl-text-3)" }}
                                    />
                                </button>
                            )}
                        </div>
                        {/* Iter74 — Bank Account triage chip. Narrows the Lines
                            grid to workers with/without bank details so the
                            payroll admin can chase down missing bank data
                            before releasing funds. Reloads via `load()`. */}
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                Bank Account
                            </span>
                            <Select value={bankAccount} onValueChange={setBankAccount}>
                                <SelectTrigger className="w-44" data-testid="payroll-period-bank-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all" data-testid="payroll-period-bank-filter-opt-all">All</SelectItem>
                                    <SelectItem value="has" data-testid="payroll-period-bank-filter-opt-has">Has bank account</SelectItem>
                                    <SelectItem value="none" data-testid="payroll-period-bank-filter-opt-none">No bank account</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {/* Iter74.7 — Amount-to-pay triage chip. Display filter
                            only — period totals, funding, and exports are
                            computed against the FULL line set (compliance
                            note in the backend list_lines handler). */}
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">
                                Amount to Pay
                            </span>
                            <Select value={amountToPay} onValueChange={setAmountToPay}>
                                <SelectTrigger className="w-44" data-testid="payroll-period-amount-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all" data-testid="payroll-period-amount-filter-opt-all">All</SelectItem>
                                    <SelectItem value="has" data-testid="payroll-period-amount-filter-opt-has">Has amount to pay</SelectItem>
                                    <SelectItem value="zero" data-testid="payroll-period-amount-filter-opt-zero">Zero pay only</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <TabsContent value="lines">
                        <LinesTable lines={filteredLines} period={period} canEdit={canEdit} canMarkPaid={canMarkPaidPerm} canRecompute={canRecompute} onChange={load} />
                        {/* Iter60.p51 — Monthly Salaries section. Filters
                            the same lines to Non-Casual monthly-rule rows
                            so the salary week's payout + accrual weeks
                            surface with a legend explaining the model.
                            Iter83 — Only rendered when the period IS the
                            last casual week AND the sibling Non-Casual
                            Monthly is unpaid. When it's paid, the
                            IterAlreadyPaidBanner surfaces instead. */}
                        <MonthlySalariesSection
                            lines={filteredLines}
                            pathModuleCode={pathModuleCode}
                            period={period}
                        />
                        <NonCasualBundlingBanner
                            period={period}
                            pathModuleCode={pathModuleCode}
                        />
                    </TabsContent>
                    <TabsContent value="cost-by-project">
                        <CostByProjectTab periodId={id} search={search} />
                    </TabsContent>
                    <TabsContent value="warnings">
                        <WarningsTab
                            structured={filteredWarningDetails}
                            legacyStrings={filteredWarnings}
                            hasStructured={rawWarningDetails.length > 0}
                            rawCount={rawWarnings.length}
                            search={search}
                            moduleCode={pathModuleCode}
                            period={period}
                            onNavigate={navigate}
                        />
                    </TabsContent>
                    <TabsContent value="funding">
                        <div className="asl-card p-4 text-sm" data-testid="funding-tab">
                            {!funding ? <div className="text-[color:var(--asl-muted)]">No funding request yet.</div>
                                : <div className="grid grid-cols-2 gap-3">
                                    <Fact label="Status" value={funding.status} />
                                    <Fact label="Bank ref" value={funding.bank_reference || "—"} />
                                    <Fact label="Wages requested" value={fmtUSD(funding.requested_amount_worker_wages)} />
                                    <Fact label="Mgmt fee" value={fmtUSD(funding.requested_amount_management_fee)} />
                                    <Fact label="Total requested" value={fmtUSD(funding.requested_amount_total)} />
                                    <Fact label="Funds received" value={funding.funds_received_amount ? fmtUSD(funding.funds_received_amount) : "—"} />
                                </div>}
                        </div>
                    </TabsContent>
                    <TabsContent value="audit">
                        <PeriodAuditTab periodId={id} />
                    </TabsContent>
                </Tabs>
            </section>
            <BulkLineItemDialog
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                periodId={id}
                moduleId={period.module_id}
                lineCount={lines.length}
                onApplied={load}
            />
            {/* Iter84 — Super Admin destructive-action confirmation.
                Requires the operator to type `DELETE` exactly + a
                non-empty reason. Everything is admin-gated on the
                backend too — this dialog is defence in depth. */}
            <Dialog
                open={iter84DeleteOpen}
                onOpenChange={(o) => { if (!o) setIter84DeleteOpen(false); }}
            >
                <DialogContent className="max-w-md" data-testid="iter84-admin-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete this {period.status} period?</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                            <div className="font-semibold">Super Admin only — destructive action.</div>
                            <ul className="list-disc ml-4 mt-1 space-y-0.5">
                                <li>Period moves to Trash (soft delete).</li>
                                <li>All payroll lines are hidden from exports and dashboards.</li>
                                <li>Restore is available from
                                    <span className="mono ml-1">Admin → Payroll Trash</span>
                                    within 30 days.</li>
                                <li>After 30 days, permanent purge is available (paranoid mode).</li>
                            </ul>
                        </div>
                        <label className="block">
                            <span className="text-xs text-[color:var(--asl-muted)]">
                                Type <strong className="mono">DELETE</strong> to confirm:
                            </span>
                            <input
                                type="text"
                                value={iter84DeletePhrase}
                                onChange={(e) => setIter84DeletePhrase(e.target.value)}
                                className="mt-1 w-full border rounded-md px-2 py-1 text-sm mono"
                                data-testid="iter84-admin-delete-phrase"
                                autoFocus
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs text-[color:var(--asl-muted)]">
                                Reason (required, 3–400 chars):
                            </span>
                            <textarea
                                value={iter84DeleteReason}
                                onChange={(e) => setIter84DeleteReason(e.target.value)}
                                rows={3}
                                maxLength={400}
                                className="mt-1 w-full border rounded-md px-2 py-1 text-sm"
                                data-testid="iter84-admin-delete-reason"
                                placeholder="e.g. Duplicate period created in error — deleting to restore correct one"
                            />
                        </label>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIter84DeleteOpen(false)}
                            disabled={iter84Deleting}
                            data-testid="iter84-admin-delete-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={iter84Delete}
                            disabled={
                                iter84Deleting
                                || iter84DeletePhrase !== "DELETE"
                                || iter84DeleteReason.trim().length < 3
                            }
                            data-testid="iter84-admin-delete-confirm"
                        >
                            {iter84Deleting ? "Deleting…" : "Delete permanently"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}

function LinesTable({ lines, period, canEdit, canMarkPaid, canRecompute, onChange }) {
    const { moduleCode: pathModuleCode } = useParams();
    const [editing, setEditing] = useState(null);
    const [confirmRecompute, setConfirmRecompute] = useState(null); // holds the line to recompute
    const [recomputing, setRecomputing] = useState(false);
    // Iter59.p15 — Dynamic project columns. Fetch the module's ACTIVE
    // projects once for the header, plus include any inactive project that
    // still shows up in a line's project_days map (historical accuracy).
    const [projects, setProjects] = useState([]);
    useEffect(() => {
        if (!period?.module_id) return;
        (async () => {
            try {
                const { data } = await api.get("/projects", { params: { module_id: period.module_id, active: true } });
                setProjects(Array.isArray(data) ? data : []);
            } catch {
                setProjects([]);
            }
        })();
    }, [period?.module_id]);
    // Iter59.p15 — Merge any deleted/inactive projects that lines still
    // reference so we don't drop historical worker-days columns silently.
    // Iter59.p16 — Union of keys from BOTH `project_cost` and
    // `project_days` — a cell can carry a $ amount from `project_cost`
    // even if we later drop `project_days` for a project.
    // The "— No project —" bucket is keyed as "None" (or historic "null")
    // and is intentionally NOT shown as a column here — it lives on the
    // Cost by Project tab only.
    const projectColumns = useMemo(() => {
        const active = projects.map((p) => ({
            id: p.id,
            name: p.name || p.code,
            // Iter60.p49.1 Gap 3 — Job # (project code) captured so the
            // column header renders as `{Job#} · {Name}` for parity with
            // the Cost by Project tab, ProjectSplitChip and CSV exports.
            code: p.code || "",
            deleted: !!p.deleted_at,
        }));
        const activeIds = new Set(active.map((p) => p.id));
        const NO_PROJECT_SENTINELS = new Set(["None", "null", ""]);
        const historic = new Set();
        for (const ln of lines) {
            const pd = ln.project_days || {};
            const pc = ln.project_cost || {};
            for (const pid of [...Object.keys(pd), ...Object.keys(pc)]) {
                if (pid && !NO_PROJECT_SENTINELS.has(pid) && !activeIds.has(pid)) historic.add(pid);
            }
        }
        for (const pid of historic) {
            active.push({ id: pid, name: `(deleted)`, code: "", deleted: true });
        }
        // Iter59.p15 — cap visible columns at 5 to keep the table readable
        // on 1366px screens; the rest is available via horizontal scroll on
        // the parent container.
        return active.slice(0, 5);
    }, [projects, lines]);
    const markPaid = async (line) => {
        try {
            await api.post(`/payroll/lines/${line.id}/mark-paid`, { payment_method: "bank_transfer", payment_reference: `MAN-${line.employee_code}` });
            toast.success("Marked paid"); onChange();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    // Iter53 Bug B — Per-row recompute. If the line has active manual
    // overrides, open a proper confirmation Dialog; otherwise run silently.
    const requestRecompute = (line) => {
        const overrideCount = Object.keys(line.manual_overrides || {}).length;
        if (overrideCount > 0) {
            setConfirmRecompute(line);
        } else {
            doRecompute(line);
        }
    };
    const doRecompute = async (line) => {
        setRecomputing(true);
        try {
            await api.post(`/payroll/lines/${line.id}/recompute`);
            toast.success("Recomputed from attendance");
            setConfirmRecompute(null);
            onChange();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRecomputing(false); }
    };
    return (
        <>
            <LineEditDrawer line={editing} onClose={() => setEditing(null)} onSaved={onChange} canRecompute={canRecompute} canEditLine={canEdit} />
            {/* Iter60.p43.4 — Sticky header + inner vertical scroll so
                the horizontal scrollbar stays pinned to the bottom of
                the viewport, not the very bottom of a 117-row table. */}
            <div className="asl-card asl-table-scroll" data-testid="lines-table-scroll">
                <table className="asl-table asl-table--zebra" data-testid="lines-table">
                    <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                        style={{ background: "var(--asl-navy-50)" }}>
                        <tr>
                            <th className="text-left px-3 py-2">Employee</th>
                            {/* Iter60.p43.5 — Module ID column between
                                Employee identity and Cat. Same source
                                as the Attendance grid's Module ID
                                column (employees.module_employee_id). */}
                            <th
                                className="text-left px-3 py-2 whitespace-nowrap"
                                data-testid="lines-col-module-id"
                            >
                                Module ID
                            </th>
                            <th className="text-left px-3 py-2" data-testid="lines-col-type">Type</th>
                            {/* Iter66 — Wage Rate column right after Type
                                so managers can eyeball hours × rate = pay
                                without opening the profile. */}
                            <th
                                className="text-right px-3 py-2 whitespace-nowrap"
                                data-testid="lines-col-wage-rate"
                            >
                                Wage Rate
                            </th>
                            <th className="text-right px-3 py-2">Present</th>
                            {projectColumns.map((p) => (
                                <th
                                    key={p.id}
                                    className={`text-right px-2 py-2 whitespace-nowrap ${p.deleted ? "line-through opacity-60" : ""}`}
                                    style={{ minWidth: "104px" }}
                                    title={p.deleted ? `${p.name} (deleted project)` : (p.code ? `Job # ${p.code} · ${p.name}` : p.name)}
                                    data-testid={`lines-col-project-${p.id}`}
                                >
                                    {/* Iter60.p49.1 Gap 3 — Column header
                                        prefixed with `Job #` code (compact
                                        rendering: `{code}·{name}`) so the
                                        UI matches the CSV export + Cost
                                        by Project tab. Falls back to bare
                                        name when a bucket has no code
                                        (e.g. deleted-project sentinel). */}
                                    {p.code ? (
                                        <span className="inline-flex items-center gap-1 justify-end">
                                            <span className="mono text-[10px] asl-crimson-text font-bold">{p.code}</span>
                                            <span className="text-[10px] opacity-70">·</span>
                                            <span>{p.name}</span>
                                        </span>
                                    ) : (
                                        p.name
                                    )}
                                </th>
                            ))}
                            <th className="text-right px-3 py-2">OT hrs</th>
                            <th className="text-right px-3 py-2">Gross</th>
                            <th className="text-right px-3 py-2">+Allow</th>
                            <th className="text-right px-3 py-2">−Deduct</th>
                            <th className="text-right px-3 py-2">Net</th>
                            {/* Iter66 — Bank Account column right after Net
                                so payroll operators can verify the wire
                                destination at a glance. Iter66.1 — Header
                                right-aligned to mirror the numeric column
                                treatment on Net Payable. */}
                            <th
                                className="text-right px-3 py-2 whitespace-nowrap"
                                data-testid="lines-col-bank-account"
                            >
                                Bank Account
                            </th>
                            {/* Iter60.p26 — Management Fee column (10% of Net).
                                Sits between Net and Status so it reads as
                                an accounting note next to the payable value. */}
                            <th
                                className="text-right px-3 py-2 whitespace-nowrap"
                                data-testid="payroll-lines-mgmt-fee-header"
                            >
                                Mgmt Fee
                            </th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((l) => (
                            <tr key={l.id} className="border-t" style={{ borderColor: "var(--asl-border)" }} data-testid={`line-row-${l.employee_code}`}>
                                <td className="px-3 py-2">
                                    <div className="font-medium asl-navy-text">
                                        <DeletedRowDecorator deleted={!!l.deleted_at}>
                                            {l.employee_id ? (
                                                <EntityLink to={`/m/${pathModuleCode}/staff/${l.employee_id}`} testid={`pp-emp-link-${l.id}`}>{l.employee_name}</EntityLink>
                                            ) : l.employee_name}
                                        </DeletedRowDecorator>
                                        {l.deleted_at && <DeletedPill deletedAt={l.deleted_at} />}
                                    </div>
                                    {/* Iter60.p49 Item B — EMP-#### removed from the visible cell.
                                        Module ID is rendered in its own column to the right. */}
                                </td>
                                {/* Iter60.p43.5 — Module ID cell.
                                    Iter60.p52 — rendered via <ModuleIdChip/>
                                    (crimson pill, same treatment as Leave
                                    Balances / Attendance grid) so the ID
                                    is legible at a glance. */}
                                <td
                                    className="px-3 py-2 whitespace-nowrap"
                                    data-testid={`payroll-line-module-id-${l.id}`}
                                >
                                    <ModuleIdChip
                                        value={l.module_employee_id}
                                        testid={`payroll-line-module-id-chip-${l.id}`}
                                    />
                                </td>
                                <td className="px-3 py-2 text-xs">
                                    <WorkerTypeBadge
                                        category={l.worker_category}
                                        testid={`payroll-line-type-${l.id}`}
                                    />
                                </td>
                                {/* Iter66 — Wage Rate cell right after Type. */}
                                <td
                                    className="px-3 py-2 mono text-xs text-right whitespace-nowrap"
                                    data-testid={`payroll-wage-rate-${l.id}`}
                                >
                                    {formatWageRate(l.wage_rate, l.wage_rate_unit)}
                                </td>
                                <td className="px-3 py-2 mono text-xs text-right">{l.attendance_summary?.present_days || 0}</td>
                                {projectColumns.map((p) => {
                                    const cost = (l.project_cost || {})[p.id];
                                    const days = (l.project_days || {})[p.id];
                                    const hasCost = cost !== undefined && cost !== null && Number(cost) !== 0;
                                    const tip = hasCost
                                        ? `${days ? `${days} day${days === 1 ? "" : "s"} · ` : ""}${fmtUSD(cost)}`
                                        : undefined;
                                    return (
                                        <td
                                            key={p.id}
                                            className="px-2 py-2 mono text-xs text-right whitespace-nowrap"
                                            style={{ minWidth: "96px" }}
                                            title={tip}
                                            data-testid={`line-project-cost-${l.employee_code}-${p.id}`}
                                        >
                                            {hasCost ? fmtUSD(cost) : "—"}
                                        </td>
                                    );
                                })}
                                <td className="px-3 py-2 mono text-xs text-right">{l.attendance_summary?.overtime_hours || 0}</td>
                                <td className="px-3 py-2 mono text-xs text-right">{fmtUSD(l.gross_total)}</td>
                                <td className="px-3 py-2 mono text-xs text-right text-emerald-700">{fmtUSD(l.allowances_total)}</td>
                                <td className="px-3 py-2 mono text-xs text-right">
                                    <DeductionBreakdown
                                        lineId={l.id}
                                        total={l.deductions_total}
                                        deductions={l.deductions}
                                    />
                                </td>
                                <td className="px-3 py-2 mono text-xs text-right font-semibold asl-navy-text">{fmtUSD(l.net_payable)}</td>
                                {/* Iter66 — Bank Account cell right after Net.
                                    Iter66.1 — Account-number-only (bank name
                                    lives on the profile). Styled identical to
                                    the Net Payable cell — bold navy, mono,
                                    right-aligned — so the routing digits line
                                    up visually with the payout amount. */}
                                <td
                                    className="px-3 py-2 mono text-xs text-right font-semibold asl-navy-text whitespace-nowrap"
                                    data-testid={`payroll-bank-account-${l.id}`}
                                >
                                    {l.bank_display ? (
                                        l.bank_display
                                    ) : (
                                        <span className="text-neutral-400 font-normal">—</span>
                                    )}
                                </td>
                                {/* Iter60.p26 — Per-line Management Fee. */}
                                <td
                                    className="px-3 py-2 mono text-xs text-right text-slate-600"
                                    data-testid={`payroll-line-mgmt-fee-${l.id}`}
                                >
                                    {fmtUSD(l.management_fee)}
                                </td>
                                <td className="px-3 py-2 text-xs">{l.payment_status}</td>
                                <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        {canEdit && ["draft", "prepared", "dept_confirmed"].includes(period.status) && !l.deleted_at &&
                                            <Button size="sm" variant="outline" onClick={() => setEditing(l)} data-testid={`line-edit-${l.employee_code}`}>Edit</Button>}
                                        {canRecompute && ["draft", "prepared", "dept_confirmed"].includes(period.status) && !l.deleted_at &&
                                            <Button size="sm" variant="outline" onClick={() => requestRecompute(l)} data-testid={`line-recompute-${l.employee_code}`} title="Recompute this line from attendance">
                                                <RefreshCw className="w-3 h-3" />
                                            </Button>}
                                        {canMarkPaid && l.payment_status === "released" && !l.deleted_at &&
                                            <Button size="sm" variant="outline" onClick={() => markPaid(l)} data-testid={`line-pay-${l.employee_code}`}>Mark Paid</Button>}
                                        <RowActions
                                            entity="payroll_line"
                                            endpoint="/payroll/lines"
                                            id={l.id}
                                            label={l.module_employee_id || l.employee_name || "line"}
                                            deleted={!!l.deleted_at}
                                            moduleId={l.module_id}
                                            hardDeleteSupported={false}
                                            onChange={onChange}
                                            testid={`line-actions-${l.employee_code}`}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Iter53 Bug B — Confirmation modal for per-row recompute when overrides exist */}
            <Dialog open={!!confirmRecompute} onOpenChange={(o) => !o && setConfirmRecompute(null)}>
                <DialogContent data-testid="recompute-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>Discard manual adjustments?</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-slate-700">
                        This line has <strong data-testid="recompute-override-count">{Object.keys(confirmRecompute?.manual_overrides || {}).length}</strong> manual override(s). Recomputing will discard them and pull fresh values from attendance.
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmRecompute(null)} data-testid="recompute-cancel">Cancel</Button>
                        <Button
                            className="asl-crimson-bg text-white"
                            onClick={() => doRecompute(confirmRecompute)}
                            disabled={recomputing}
                            data-testid="recompute-confirm"
                        >
                            {recomputing ? "Recomputing…" : "Recompute anyway"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function LineEditDrawer({ line, onClose, onSaved, canRecompute, canEditLine }) {
    const { moduleCode: pathModuleCode } = useParams();
    const [allow, setAllow] = useState([]);
    const [ded, setDed] = useState([]);
    const [current, setCurrent] = useState(line);
    useEffect(() => {
        if (line) {
            setCurrent(line);
            setAllow(line.allowances || []);
            setDed(line.deductions || []);
        }
    }, [line]);
    if (!line) return null;
    const refresh = async () => {
        try {
            const { data } = await api.get(`/payroll/lines/${line.id}`);
            setCurrent(data);
            onSaved?.();
        } catch { /* noop */ }
    };
    const save = async () => {
        try {
            await api.patch(`/payroll/lines/${line.id}`, { allowances: allow, deductions: ded });
            toast.success("Line updated"); onClose(); onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/30" onClick={onClose} data-testid="line-edit-drawer">
            <div className="bg-white w-full max-w-2xl h-full p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold asl-navy-text">
                    {line.employee_id ? (
                        <EntityLink to={`/m/${pathModuleCode}/staff/${line.employee_id}`} testid="line-edit-emp-link">
                            {line.employee_name}
                        </EntityLink>
                    ) : line.employee_name}
                </h3>
                <div className="text-xs mono asl-crimson-text mb-4">
                    {line.module_employee_id ? `${line.module_employee_id} · ` : ""}Net {fmtUSD(current?.net_pay ?? current?.net_payable ?? line.net_payable)}
                </div>

                {/* Iter60.p26 — Read-only Management Fee (10% of Net). Not
                    editable — always derived from Net whenever the line is
                    recomputed / patched / overridden. */}
                <div
                    className="mb-4 text-xs px-3 py-2 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-between"
                    data-testid="line-edit-mgmt-fee"
                >
                    <span className="text-[color:var(--asl-muted)] uppercase tracking-wider">
                        Management Fee ({Math.round(((current?.management_fee_rate ?? line.management_fee_rate ?? 0.10) * 100))}% of Net · read-only)
                    </span>
                    <span className="mono font-semibold text-slate-700">
                        {fmtUSD(current?.management_fee ?? line.management_fee ?? 0)}
                    </span>
                </div>

                {/* Iter53 — Editable breakdown card with per-component override + audit + recompute */}
                <PayrollLineBreakdown
                    line={current || line}
                    canEdit={canEditLine}
                    canRecompute={canRecompute}
                    onChanged={refresh}
                />

                <div className="mt-6 border-t pt-4">
                    <AdjustmentList title="Allowances (add-ons)" items={allow} setItems={setAllow} testid="allowances" />
                    <AdjustmentList title="Ad-hoc deductions" items={ded} setItems={setDed} testid="deductions" />
                </div>
                <div className="flex gap-2 mt-4">
                    <Button variant="outline" onClick={onClose}>Close</Button>
                    <Button className="asl-navy-bg text-white" onClick={save} data-testid="line-edit-save">Save changes</Button>
                </div>
            </div>
        </div>
    );
}

function AdjustmentList({ title, items, setItems, testid }) {
    const add = () => setItems([...items, { label: "", amount: 0, notes: "" }]);
    const upd = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: k === "amount" ? Number(v) : v } : it));
    const rm = (i) => setItems(items.filter((_, idx) => idx !== i));
    return (
        <div className="mt-4">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold asl-navy-text">{title}</h4>
                <Button size="sm" variant="outline" onClick={add} data-testid={`${testid}-add`}>+ Add</Button>
            </div>
            <div className="space-y-2 mt-2">
                {items.map((it, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`${testid}-row-${i}`}>
                        <input className="border rounded px-2 py-1 text-xs flex-1" placeholder="Label"
                            value={it.label} onChange={(e) => upd(i, "label", e.target.value)} />
                        <input type="number" min="0" step="0.01" className="border rounded px-2 py-1 text-xs w-24"
                            value={it.amount} onChange={(e) => upd(i, "amount", e.target.value)} />
                        <button onClick={() => rm(i)} className="text-rose-700 text-xs">×</button>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Stat({ label, value, accent, testid }) {
    return (
        <div
            className="rounded-lg p-3"
            style={{ background: accent ? "var(--asl-navy-50)" : "white", border: "1px solid var(--asl-border)" }}
            data-testid={testid}
        >
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">{label}</div>
            <div className={`text-lg font-bold ${accent ? "asl-crimson-text" : "asl-navy-text"} mt-1`}>{value}</div>
        </div>
    );
}
function Fact({ label, value }) {
    return <div><div className="text-[10px] uppercase text-[color:var(--asl-muted)]">{label}</div><div className="text-sm asl-navy-text">{value}</div></div>;
}

function PeriodAuditTab({ periodId }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get(`/audit?entity_type=payroll_period&entity_id=${periodId}&limit=200`)
            .then(({ data }) => setLogs(data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [periodId]);
    if (loading) return <div className="asl-card p-4 text-sm" data-testid="period-audit-loading">Loading…</div>;
    if (logs.length === 0) return <div className="asl-card p-6 text-sm text-[color:var(--asl-muted)]" data-testid="period-audit-empty">No audit entries for this period.</div>;
    return (
        <div className="asl-card asl-table-scroll" data-testid="period-audit-tab">
            <table className="asl-table asl-table--zebra">
                <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                    style={{ background: "var(--asl-navy-50)" }}>
                    <tr>
                        <th className="text-left px-3 py-2">When</th>
                        <th className="text-left px-3 py-2">Action</th>
                        <th className="text-left px-3 py-2">Actor</th>
                    </tr>
                </thead>
                <tbody>
                    {logs.map((l) => (
                        <tr key={l.id} className="border-t" style={{ borderColor: "var(--asl-border)" }}>
                            <td className="px-3 py-2 mono text-xs">{formatDateTime(l.timestamp)}</td>
                            <td className="px-3 py-2 text-xs"><span className="px-2 py-0.5 rounded-full mono asl-navy-bg text-white">{l.action}</span></td>
                            <td className="px-3 py-2 mono text-xs">{l.actor_email}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function FundsReceivedDrawer({ open, funding, onClose, onSaved }) {
    const [amount, setAmount] = useState(0);
    const [bankRef, setBankRef] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (open && funding) {
            setAmount(funding.requested_amount_total || 0);
            const period_code = (funding.period_id || "").slice(0, 6).toUpperCase();
            const today = new Date();
            const ymd = `${String(today.getFullYear()).slice(2)}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
            setBankRef(`WIRE-${period_code}-${ymd}`);
            setNotes("");
        }
    }, [open, funding]);
    if (!open || !funding) return null;
    const submit = async () => {
        if (!(Number(amount) > 0)) { toast.error("Amount must be greater than 0"); return; }
        setSaving(true);
        try {
            await api.post(`/payroll/funding/${funding.id}/mark-funds-received`, {
                funds_received_amount: Number(amount),
                bank_reference: bankRef || null,
                notes: notes || null,
            });
            toast.success("Funds received recorded"); onClose(); onSaved();
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/30" onClick={onClose} data-testid="funds-drawer">
            <div className="bg-white w-full max-w-md h-full p-5 overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()}>
                <div>
                    <h3 className="text-lg font-bold asl-navy-text">Mark Funds Received</h3>
                    <div className="text-xs text-[color:var(--asl-muted)] mt-1">
                        Requested: <span className="mono asl-crimson-text font-semibold">{fmtUSD(funding.requested_amount_total)}</span>
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="text-[10px] uppercase">Amount received</div>
                    <input type="number" min="0" step="0.01" className="border rounded px-2 py-1.5 w-full text-sm mono"
                        value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="funds-amount" />
                </div>
                <div className="space-y-2">
                    <div className="text-[10px] uppercase">Bank reference</div>
                    <input className="border rounded px-2 py-1.5 w-full text-sm" placeholder="WIRE-001234"
                        value={bankRef} onChange={(e) => setBankRef(e.target.value)} data-testid="funds-bank-ref" />
                </div>
                <div className="space-y-2">
                    <div className="text-[10px] uppercase">Notes</div>
                    <textarea rows={2} className="border rounded px-2 py-1.5 w-full text-sm"
                        value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="funds-notes" />
                </div>
                <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="asl-navy-bg text-white" onClick={submit} disabled={saving} data-testid="funds-submit">
                        {saving ? "Saving…" : "Submit"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


// Iter60.p51 — Monthly Salaries section.
// Filters payroll lines to Non-Casual monthly rows for the currently
// selected period and renders a compact table with:
//   Module ID · Name · Job Title · Type badge · Monthly salary ·
//   Days present in the month · Project allocation · This-period pay ·
//   Non-attendance deductions · Net payable.
// Salary-week rows show the pro-rated monthly amount; accrual weeks
// render $0 with an "accrual" tag so it's visually obvious this week
// carries no cash payout for Non-Casuals.
function MonthlySalariesSection({ lines, pathModuleCode, period }) {
    const monthlyLines = useMemo(
        () => (lines || []).filter(
            (l) => l.attendance_summary?.is_non_casual_monthly === true,
        ),
        [lines],
    );
    if (monthlyLines.length === 0) return null;
    const anySalaryWeek = monthlyLines.some(
        (l) => l.attendance_summary?.is_salary_week === true,
    );
    // Iter83 — Only render the section when EITHER:
    //   1. Not a salary week at all (accrual mode — always shown), OR
    //   2. It IS the salary week AND `period.bundles_non_casual_monthly`
    //      is not explicitly false (i.e., sibling Non-Casual Monthly
    //      is missing or unpaid, so we still bundle here).
    // When the section IS the last casual week AND the sibling monthly
    // has already been paid, the section is suppressed and the
    // `NonCasualBundlingBanner` below explains what happened.
    if (
        anySalaryWeek
        && period?.is_last_casual_week_of_month === true
        && period?.bundles_non_casual_monthly === false
    ) {
        return null;
    }
    return (
        <div className="mt-6" data-testid="monthly-salaries-section">
            <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-sm font-bold asl-navy-text">
                    Monthly Salaries ({monthlyLines.length})
                </h3>
                <span
                    className="text-[10px] uppercase tracking-wider"
                    style={{ color: "var(--asl-text-3)" }}
                >
                    {anySalaryWeek ? "SALARY WEEK — end-of-month payout" : "Accrual — pay in the last week of the month"}
                </span>
            </div>
            {/* Legend — spec-quoted string. */}
            <p
                className="text-xs mb-3 px-3 py-2 rounded-md"
                data-testid="monthly-salaries-legend"
                style={{
                    background: "var(--asl-navy-50, #F5F6F8)",
                    color: "var(--asl-navy)",
                    borderLeft: "3px solid var(--asl-crimson, #A8324A)",
                }}
            >
                Non-Casuals are paid the full month at the end of each month. Attendance is recorded but does not affect pay.
            </p>
            <div className="asl-card asl-table-scroll" data-testid="monthly-salaries-table-scroll">
                <table className="asl-table asl-table--zebra" data-testid="monthly-salaries-table">
                    <thead
                        className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                        style={{ background: "var(--asl-navy-50)" }}
                    >
                        <tr>
                            <th className="text-left px-3 py-2 whitespace-nowrap">Module ID</th>
                            <th className="text-left px-3 py-2">Name</th>
                            <th className="text-left px-3 py-2">Job title</th>
                            <th className="text-left px-3 py-2">Type</th>
                            <th className="text-right px-3 py-2 whitespace-nowrap">Monthly salary</th>
                            <th className="text-right px-3 py-2 whitespace-nowrap">Days present</th>
                            <th className="text-left  px-3 py-2 whitespace-nowrap">Project split</th>
                            <th className="text-right px-3 py-2 whitespace-nowrap">This-period pay</th>
                            <th className="text-right px-3 py-2 whitespace-nowrap">Deductions</th>
                            <th className="text-right px-3 py-2">Net</th>
                        </tr>
                    </thead>
                    <tbody>
                        {monthlyLines.map((l) => {
                            const a = l.attendance_summary || {};
                            const salaryWeek = a.is_salary_week === true;
                            const monthly = Number(a.monthly_salary || 0);
                            const proFactor = Number(a.pro_rate_factor ?? 1);
                            const proRated = Math.round(monthly * proFactor * 100) / 100;
                            const splits = Object.entries(l.project_days || {})
                                .filter(([, d]) => Number(d) > 0);
                            return (
                                <tr
                                    key={l.id}
                                    className="border-t"
                                    style={{ borderColor: "var(--asl-border)" }}
                                    data-testid={`monthly-salaries-row-${l.employee_code || l.id}`}
                                >
                                    <td className="px-3 py-2 whitespace-nowrap"
                                        data-testid={`monthly-salaries-module-id-${l.id}`}>
                                        <ModuleIdChip
                                            value={l.module_employee_id}
                                            testid={`monthly-salaries-module-id-chip-${l.id}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2 asl-navy-text font-medium">
                                        {l.employee_id ? (
                                            <EntityLink
                                                to={`/m/${pathModuleCode}/staff/${l.employee_id}`}
                                                testid={`monthly-salaries-emp-link-${l.id}`}
                                            >
                                                {l.employee_name}
                                            </EntityLink>
                                        ) : l.employee_name}
                                    </td>
                                    <td className="px-3 py-2 text-xs">{l.job_title || l.wage_rate_snapshot?.job_role || "—"}</td>
                                    <td className="px-3 py-2">
                                        <WorkerTypeBadge
                                            category={l.worker_category}
                                            testid={`monthly-salaries-type-${l.id}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2 mono text-xs text-right">
                                        {fmtUSD(monthly)}
                                        {proFactor < 1 && (
                                            <span
                                                className="ml-1 text-[10px] opacity-70"
                                                title={`Pro-rated: ${a.working_days_worked}/${a.working_days_in_month} workdays`}
                                            >
                                                × {(proFactor * 100).toFixed(0)}%
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 mono text-xs text-right">
                                        {a.present_days ?? 0}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        {splits.length > 0 ? (
                                            <span className="inline-flex flex-wrap gap-1">
                                                {splits.map(([pid, days]) => (
                                                    <span
                                                        key={pid}
                                                        className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px]"
                                                        style={{
                                                            background: "var(--asl-navy-50)",
                                                            color: "var(--asl-navy)",
                                                        }}
                                                    >
                                                        {(l.project_names || {})[pid] || pid.slice(0, 6)} · {days}d
                                                    </span>
                                                ))}
                                            </span>
                                        ) : "—"}
                                    </td>
                                    <td className="px-3 py-2 mono text-xs text-right font-semibold">
                                        {salaryWeek ? (
                                            <span data-testid={`monthly-salaries-pay-${l.id}`}>{fmtUSD(proRated)}</span>
                                        ) : (
                                            <span
                                                className="text-[color:var(--asl-muted)]"
                                                title="Non-Casuals accrue $0 in this week; the monthly salary is paid in the last week of the month."
                                                data-testid={`monthly-salaries-accrual-${l.id}`}
                                            >
                                                $0.00 <span className="text-[9px] uppercase ml-1 opacity-70">accrual</span>
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 mono text-xs text-right">
                                        {fmtUSD(l.deductions_total)}
                                    </td>
                                    <td className="px-3 py-2 mono text-xs text-right font-semibold asl-navy-text">
                                        {fmtUSD(l.net_payable)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// Iter83 — Info banner shown at the bottom of the Lines tab when this
// IS the last casual week of a month AND the sibling Non-Casual Monthly
// period has already been paid. Explains where the payout was made and
// links back to that period so the manager can confirm.
function NonCasualBundlingBanner({ period, pathModuleCode }) {
    if (!period) return null;
    if (period.is_last_casual_week_of_month !== true) return null;
    if (period.bundles_non_casual_monthly !== false) return null;
    const status = period.non_casual_monthly_status;
    const siblingId = period.non_casual_monthly_period_id;
    if (!siblingId || status !== "paid") return null;
    // Human-readable month label from the period start_date.
    let monthLabel = "";
    try {
        const [y, m] = (period.start_date || "").split("-");
        if (y && m) {
            const dt = new Date(Number(y), Number(m) - 1, 1);
            monthLabel = dt.toLocaleString("en-GB", { month: "long", year: "numeric" });
        }
    } catch { /* noop */ }
    const paidAt = period.non_casual_monthly_paid_at;
    let paidAtLabel = "";
    if (paidAt) {
        try {
            paidAtLabel = new Date(paidAt).toLocaleDateString("en-GB", {
                day: "2-digit", month: "short", year: "numeric",
            });
        } catch { /* noop */ }
    }
    return (
        <div
            className="mt-6 p-3 rounded-md text-sm"
            data-testid="non-casual-bundling-banner"
            style={{
                background: "rgba(6, 95, 70, 0.08)",
                color: "rgb(6, 95, 70)",
                border: "1px solid rgba(6, 95, 70, 0.25)",
                borderLeftWidth: 3,
                borderLeftColor: "rgb(6, 95, 70)",
            }}
        >
            <div className="font-semibold mb-1">
                Non-Casual Monthly already paid
                {monthLabel ? ` — ${monthLabel}` : ""}
            </div>
            <div>
                {`The Non-Casual Monthly payout${monthLabel ? ` for ${monthLabel}` : ""}${paidAtLabel ? ` was paid on ${paidAtLabel}` : " has been paid"} via period `}
                <EntityLink
                    to={`/m/${pathModuleCode}/payroll/${siblingId}`}
                    testid="non-casual-bundling-banner-link"
                >
                    {siblingId.slice(0, 8)}
                </EntityLink>
                {`. Non-casuals appear below as $0 informational rows and are NOT bundled into this week's Fund Req.`}
            </div>
        </div>
    );
}


function CostByProjectTab({ periodId, search }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get(`/payroll/periods/${periodId}/cost-by-project`)
            .then(({ data }) => alive && setData(data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [periodId]);
    if (loading || !data) {
        return <div className="asl-card p-6 text-sm" data-testid="cbp-loading">Loading cost breakdown…</div>;
    }
    // Iter60.p43.5 → p49.1 — Client-side filter matches Job # + name.
    // Empty search returns the original list unchanged so totals +
    // palette indices remain stable in the common case.
    const searchLc = (search || "").trim().toLowerCase();
    const filteredByProject = !searchLc
        ? data.by_project
        : data.by_project.filter((r) => {
            const bag = `${r.project_code || ""} ${r.project_name || ""}`.toLowerCase();
            return bag.includes(searchLc);
        });
    const total = data.total_cost || 0;
    // Iter60.p35 Part B — % of Total now uses the all-in Total (cost + mgmt
    // fee) as the denominator so a new "Total" column can be exposed
    // between Mgmt Fee and % without breaking sum-to-100%.
    const totalAll = data.total_all || (total + (data.total_mgmt_fee || 0));
    const palette = ["#1B2A56", "#A8324A", "#F5B841", "#3B7A57", "#6C6EBF", "#B26E00", "#4B5563"];
    return (
        <div className="asl-card p-4 space-y-4" data-testid="cbp-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h3 className="text-sm font-bold asl-navy-text">Cost by Project</h3>
                    <div className="text-[11px] text-[color:var(--asl-muted)]">
                        Attendance-weighted allocation of <b>net payable</b> across projects for this period.
                    </div>
                </div>
                <a href={`${API_BASE}/payroll/periods/${periodId}/cost-by-project.csv`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" data-testid="cbp-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                </a>
            </div>

            {/* Horizontal stacked bar */}
            {total > 0 && (
                <div className="w-full h-3 rounded overflow-hidden flex" data-testid="cbp-bar">
                    {data.by_project.map((r, i) => {
                        const label = r.project_code ? `${r.project_code} · ${r.project_name}` : r.project_name;
                        return (
                            <div key={r.project_id || `none-${i}`}
                                title={`${label}: ${fmtUSD(r.cost)} (${((r.cost/total)*100).toFixed(1)}%)`}
                                style={{ width: `${(r.cost/total)*100}%`, background: palette[i % palette.length] }} />
                        );
                    })}
                </div>
            )}

            {data.by_project.length === 0 ? (
                <div className="text-sm text-[color:var(--asl-muted)]" data-testid="cbp-empty">No payroll lines in this period.</div>
            ) : filteredByProject.length === 0 ? (
                <div className="text-sm text-[color:var(--asl-muted)]" data-testid="cbp-empty">
                    No projects match your search.
                </div>
            ) : (
                /* Iter60.p43.4 — Wrap in the sticky-header scroll container. */
                <div className="asl-table-scroll" data-testid="cbp-table-scroll">
                <table className="asl-table asl-table--zebra" data-testid="cbp-table">
                    <thead className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                        style={{ background: "var(--asl-navy-50)" }}>
                        <tr>
                            <th className="text-left px-3 py-2">Project</th>
                            <th className="text-right px-3 py-2">Employees</th>
                            <th className="text-right px-3 py-2">Worker Days</th>
                            <th className="text-right px-3 py-2">Cost</th>
                            <th className="text-right px-3 py-2" data-testid="cbp-mgmt-fee-header">Mgmt Fee (10%)</th>
                            <th className="text-right px-3 py-2" data-testid="cbp-total-header">Total</th>
                            <th className="text-right px-3 py-2">% of Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredByProject.map((r, i) => {
                            const rowTotal = r.total != null ? r.total : (r.cost + (r.mgmt_fee || 0));
                            const pct = totalAll ? (rowTotal / totalAll) * 100 : 0;
                            return (
                                <tr key={r.project_id || `none-${i}`} className="border-t"
                                    style={{ borderColor: "var(--asl-border)" }}
                                    data-testid={`cbp-row-${r.project_id || 'none'}`}>
                                    <td className="px-3 py-2">
                                        <span className="inline-block w-2.5 h-2.5 rounded-sm mr-2 align-middle"
                                            style={{ background: palette[i % palette.length] }} />
                                        {/* Iter60.p49.1 — Job # prefix ({code} · {name}).
                                            Falls back to bare name if the bucket has no code
                                            (only "— No project —" bucket). */}
                                        {r.project_code ? (
                                            <>
                                                <span className="mono text-[10px] asl-crimson-text font-bold mr-1.5">{r.project_code}</span>
                                                <span className="asl-navy-text font-medium">· {r.project_name}</span>
                                            </>
                                        ) : (
                                            <span className="asl-navy-text font-medium">{r.project_name}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 mono text-xs text-right">{r.employee_count}</td>
                                    <td className="px-3 py-2 mono text-xs text-right">{r.worker_days}</td>
                                    <td className="px-3 py-2 mono text-xs text-right asl-navy-text font-semibold">{fmtUSD(r.cost)}</td>
                                    <td className="px-3 py-2 mono text-xs text-right" data-testid={`cbp-mgmt-fee-${r.project_id || 'none'}`}>{fmtUSD(r.mgmt_fee || 0)}</td>
                                    <td className="px-3 py-2 mono text-xs text-right asl-navy-text font-semibold" data-testid={`cost-by-project-total-${r.project_id || 'none'}`}>{fmtUSD(rowTotal)}</td>
                                    <td className="px-3 py-2 mono text-xs text-right">{pct.toFixed(1)}%</td>
                                </tr>
                            );
                        })}
                        <tr className="border-t font-semibold" style={{ borderColor: "var(--asl-border)" }}>
                            <td className="px-3 py-2 asl-navy-text">Total</td>
                            <td className="px-3 py-2"></td>
                            <td className="px-3 py-2"></td>
                            <td className="px-3 py-2 mono text-xs text-right asl-crimson-text" data-testid="cbp-total">{fmtUSD(total)}</td>
                            <td className="px-3 py-2 mono text-xs text-right asl-crimson-text" data-testid="cbp-total-mgmt-fee">{fmtUSD(data.total_mgmt_fee || 0)}</td>
                            <td className="px-3 py-2 mono text-xs text-right asl-crimson-text" data-testid="cost-by-project-total-sum">{fmtUSD(totalAll)}</td>
                            <td className="px-3 py-2 mono text-xs text-right">100.0%</td>
                        </tr>
                    </tbody>
                </table>
                </div>
            )}
        </div>
    );
}


// Iter58 Part C — Bulk allowance / deduction on a payroll period.
// Iter60.p56 — Optional Project scope selector + live preview endpoint.
function BulkLineItemDialog({ open, onOpenChange, periodId, moduleId, lineCount, onApplied }) {
    const [form, setForm] = useState({
        kind: "allowance", label: "", amount: "", category: "all", projectId: "",
    });
    const [saving, setSaving] = useState(false);
    // Iter60.p56 — Project options + live-typed search + resolved preview.
    const [projects, setProjects] = useState([]);
    const [projectQuery, setProjectQuery] = useState("");
    const [projectMenuOpen, setProjectMenuOpen] = useState(false);
    // { matched_count, sample_names, employee_ids } — populated by the
    // /preview endpoint. `null` means "not fetched yet".
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ kind: "allowance", label: "", amount: "", category: "all", projectId: "" });
            setProjectQuery("");
            setProjectMenuOpen(false);
            setPreview(null);
        }
    }, [open]);

    // Iter60.p56 — Fetch projects for the current module once the
    // dialog opens. Reuses `/api/projects` — `search` param is unused
    // here; we filter in-memory so the user gets instant feedback as
    // they type.
    useEffect(() => {
        if (!open || !moduleId) return;
        (async () => {
            try {
                const { data } = await api.get("/projects", {
                    params: { module_id: moduleId, active: true },
                });
                setProjects(Array.isArray(data) ? data : []);
            } catch (e) {
                // Silent — projects are optional; category-only bulk still works.
                setProjects([]);
            }
        })();
    }, [open, moduleId]);

    // Iter60.p56 — Refetch preview whenever the category / project
    // filter changes. Debounce keeps typing-latency low; the endpoint
    // is idempotent and cheap.
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(async () => {
            setPreviewLoading(true);
            try {
                const params = { category: form.category === "all" ? "all" : form.category };
                if (form.projectId) params.project_id = form.projectId;
                const { data } = await api.get(
                    `/payroll/periods/${periodId}/bulk-allowance/preview`,
                    { params },
                );
                setPreview(data);
            } catch (e) {
                setPreview(null);
            } finally {
                setPreviewLoading(false);
            }
        }, 200);
        return () => clearTimeout(t);
    }, [open, form.category, form.projectId, periodId]);

    // Iter60.p56 — Filtered project options (matches number OR name).
    const filteredProjects = useMemo(() => {
        const q = projectQuery.trim().toLowerCase();
        if (!q) return projects.slice(0, 25);
        return projects
            .filter((p) => {
                const code = (p.code || "").toLowerCase();
                const name = (p.name || "").toLowerCase();
                return code.includes(q) || name.includes(q);
            })
            .slice(0, 25);
    }, [projects, projectQuery]);

    const selectedProject = useMemo(() => {
        if (!form.projectId) return null;
        return projects.find((p) => p.id === form.projectId) || null;
    }, [projects, form.projectId]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.label.trim()) { toast.error("Label is required"); return; }
        const raw = form.amount === "" || form.amount === "-" ? NaN : Number(form.amount);
        // Iter59.p1 — accept negatives (reversals); reject exactly zero client-side
        // so admins don't see a confusing backend toast for a preventable case.
        if (!Number.isFinite(raw)) { toast.error("Amount must be a number"); return; }
        if (raw === 0) {
            toast.error("Amount cannot be zero — use a positive value to add, negative to reverse.");
            return;
        }
        setSaving(true);
        try {
            const filter = {};
            if (form.category !== "all") filter.category = form.category;
            if (form.projectId) filter.project_id = form.projectId;
            const payload = {
                kind: form.kind,
                label: form.label.trim(),
                amount: raw,
                filter,
            };
            const { data } = await api.post(`/payroll/periods/${periodId}/bulk-line-item`, payload);
            const verb = raw < 0 ? "reversed" : "applied";
            toast.success(`${data.applied} line(s) ${verb}`);
            onOpenChange(false);
            onApplied?.();
        } catch (err) {
            // Iter59.p1 — surface the real server error. formatApiError picks
            // up `detail` (string OR Pydantic list). 400 / 422 must NOT be
            // mislabeled as permission failures.
            toast.error(formatApiError(err));
        } finally { setSaving(false); }
    };

    // Iter59.p1 — Live impact preview so admin sees exactly what will happen.
    const previewAmt = form.amount === "" || form.amount === "-" ? NaN : Number(form.amount);
    const isReversal = Number.isFinite(previewAmt) && previewAmt < 0;
    const isZero = previewAmt === 0;
    // Iter60.p56 — Impact preview copy now honours the live employee
    // count returned by `/bulk-allowance/preview`.
    const matched = preview?.matched_count ?? null;
    const sampleNames = preview?.sample_names || [];
    const categoryLabel = form.category === "all"
        ? "all categories"
        : form.category === "casual" ? "Casuals only"
        : form.category === "non_casual_manual" ? "Non-Casual · Manual only"
        : form.category === "non_casual_non_manual" ? "Non-Casual · Non-Manual only"
        : form.category;
    const projectLabel = selectedProject
        ? `project ${(selectedProject.code || "").trim() || "(no #)"} — ${selectedProject.name || "?"}`
        : "all projects";
    const targetSummary = matched === null
        ? (previewLoading ? "…" : "?")
        : `${matched} employee${matched === 1 ? "" : "s"}`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="bulk-line-item-dialog">
                <DialogHeader>
                    <DialogTitle>Bulk {form.kind === "allowance" ? "allowance" : "deduction"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Kind</div>
                            <select className="border rounded px-2 py-1.5 w-full text-sm bg-white"
                                value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                                data-testid="bulk-kind">
                                <option value="allowance">Allowance</option>
                                <option value="deduction">Deduction</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Category filter</div>
                            <select className="border rounded px-2 py-1.5 w-full text-sm bg-white"
                                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                                data-testid="bulk-category">
                                <option value="all">All employees ({lineCount})</option>
                                <option value="casual">Casual only</option>
                                <option value="non_casual_manual">Non-Casual Manual</option>
                                <option value="non_casual_non_manual">Non-Casual Non-Manual</option>
                            </select>
                        </div>
                    </div>
                    {/* Iter60.p56 — Project searchable single-select. Empty = all projects. */}
                    <div className="space-y-1 relative">
                        <div className="text-[10px] uppercase text-[color:var(--asl-muted)] flex items-center justify-between">
                            <span>Project (optional)</span>
                            {form.projectId && (
                                <button
                                    type="button"
                                    className="text-[10px] font-medium text-[color:var(--asl-crimson)] hover:underline"
                                    onClick={() => { setForm({ ...form, projectId: "" }); setProjectQuery(""); }}
                                    data-testid="bulk-project-clear"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <input
                            type="text"
                            className="border rounded px-2 py-1.5 w-full text-sm"
                            placeholder="Search by number or name (e.g. 388 or workshop)"
                            value={selectedProject && !projectMenuOpen
                                ? `${(selectedProject.code || "").trim() || "—"} — ${selectedProject.name || ""}`
                                : projectQuery}
                            onFocus={() => setProjectMenuOpen(true)}
                            onChange={(e) => { setProjectQuery(e.target.value); setProjectMenuOpen(true); }}
                            onBlur={() => setTimeout(() => setProjectMenuOpen(false), 150)}
                            data-testid="bulk-project-search"
                        />
                        {projectMenuOpen && (
                            <div
                                className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md border bg-white shadow-lg"
                                data-testid="bulk-project-menu"
                            >
                                {filteredProjects.length === 0 && (
                                    <div className="px-3 py-2 text-xs italic text-[color:var(--asl-muted)]">
                                        No projects match. Leave empty to include all.
                                    </div>
                                )}
                                {filteredProjects.map((p) => (
                                    <button
                                        type="button"
                                        key={p.id}
                                        onMouseDown={(e) => { e.preventDefault(); }}
                                        onClick={() => {
                                            setForm({ ...form, projectId: p.id });
                                            setProjectQuery("");
                                            setProjectMenuOpen(false);
                                        }}
                                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[color:var(--asl-navy-50)] ${
                                            form.projectId === p.id ? "bg-[color:var(--asl-navy-50)]" : ""
                                        }`}
                                        data-testid={`bulk-project-opt-${p.id}`}
                                    >
                                        <span className="font-semibold mono">
                                            {(p.code || "").trim() || "—"}
                                        </span>
                                        <span className="mx-1 opacity-50">—</span>
                                        <span>{p.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="space-y-1">
                        <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Label *</div>
                        <input className="border rounded px-2 py-1.5 w-full text-sm" required
                            placeholder={form.kind === "allowance" ? "e.g. Transport Allowance" : "e.g. Uniform Fee"}
                            value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                            data-testid="bulk-label" />
                    </div>
                    <div className="space-y-1">
                        <div className="text-[10px] uppercase text-[color:var(--asl-muted)]">Amount per employee *</div>
                        {/* Iter59.p1 — no min attribute; negative amounts are the reversal path. */}
                        <input className="border rounded px-2 py-1.5 w-full text-sm mono" required type="number" step="0.01"
                            value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                            placeholder="positive to add · negative to reverse"
                            data-testid="bulk-amount" />
                        <div className="text-[10px] text-[color:var(--asl-muted)]">
                            Enter a negative amount to reverse a previously-applied {form.kind}.
                        </div>
                    </div>
                    {/* Iter59.p1 / p56 — Live impact preview. Reversal / zero paths get their own color. */}
                    <div
                        className={`rounded-md border px-3 py-2 text-xs space-y-1 ${
                            isZero
                                ? "border-rose-300 bg-rose-50 text-rose-900"
                                : isReversal
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                                    : "border-amber-300 bg-amber-50 text-amber-900"
                        }`}
                        data-testid="bulk-preview-hint"
                    >
                        {isZero ? (
                            <span data-testid="bulk-preview-zero">
                                Amount cannot be zero — use a positive value to add, negative to reverse.
                            </span>
                        ) : Number.isFinite(previewAmt) ? (
                            <>
                                <div data-testid={isReversal ? "bulk-preview-reversal" : "bulk-preview-normal"}>
                                    Net effect per employee:{" "}
                                    <strong className="mono">
                                        {isReversal ? "−" : "+"}${Math.abs(previewAmt).toFixed(2)}
                                    </strong>{" "}
                                    ({isReversal ? `reverses a previous ${form.kind}` : form.kind}).{" "}
                                    Applied to <strong data-testid="bulk-preview-target">{targetSummary}</strong>
                                    {" "}({categoryLabel}, {projectLabel}).
                                </div>
                                {sampleNames.length > 0 && (
                                    <div className="text-[10px] opacity-75" data-testid="bulk-preview-samples">
                                        e.g. {sampleNames.join(", ")}
                                        {matched > sampleNames.length ? ", …" : ""}
                                    </div>
                                )}
                                {Number.isFinite(previewAmt) && matched !== null && matched > 0 && (
                                    <div className="text-[10px] opacity-75" data-testid="bulk-preview-total">
                                        Total impact:{" "}
                                        <strong className="mono">
                                            {isReversal ? "−" : "+"}${(Math.abs(previewAmt) * matched).toFixed(2)}
                                        </strong>
                                    </div>
                                )}
                                {!isReversal && (
                                    <div className="text-[10px] opacity-75">
                                        Recurring templates are unaffected.
                                    </div>
                                )}
                            </>
                        ) : (
                            <span>Enter an amount to see the impact preview.</span>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" className="asl-navy-bg text-white"
                            disabled={saving || isZero || !Number.isFinite(previewAmt) || matched === 0}
                            data-testid="bulk-submit">
                            {saving ? "Applying…" : (isReversal ? "Reverse on lines" : "Apply to lines")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}


// Iter60.p49 Item A / p49.1 Gap 2 — Structured Warnings tab. Prefers
// `warning_details` (per-worker cards with a "Fix" CTA that deep-links
// to Wage Rates with prefill query params) and falls back to flat
// string bullets for legacy periods that predate p49 AND cannot be
// reconstructed from parseable strings on the backend.
const WARNING_KIND_META = {
    skipped_no_rate: {
        label: "No wage rate",
        bg: "rgba(239, 68, 68, 0.08)",
        border: "rgba(239, 68, 68, 0.4)",
        chipBg: "rgba(239, 68, 68, 0.16)",
        chipFg: "#b91c1c",
    },
    skipped_scope_mismatch: {
        label: "Excluded by scope",
        bg: "rgba(217, 119, 6, 0.06)",
        border: "rgba(217, 119, 6, 0.35)",
        chipBg: "rgba(217, 119, 6, 0.18)",
        chipFg: "#b45309",
    },
    skipped_status_inactive: {
        label: "Inactive employee",
        bg: "rgba(217, 119, 6, 0.06)",
        border: "rgba(217, 119, 6, 0.35)",
        chipBg: "rgba(217, 119, 6, 0.18)",
        chipFg: "#b45309",
    },
    unapproved_days: {
        label: "Unapproved days",
        bg: "rgba(217, 119, 6, 0.06)",
        border: "rgba(217, 119, 6, 0.35)",
        chipBg: "rgba(217, 119, 6, 0.18)",
        chipFg: "#b45309",
    },
    legacy_unstructured: {
        label: "Warning",
        bg: "rgba(148, 163, 184, 0.10)",
        border: "rgba(148, 163, 184, 0.45)",
        chipBg: "rgba(100, 116, 139, 0.18)",
        chipFg: "#334155",
    },
};

function WarningsTab({ structured, legacyStrings, hasStructured, rawCount, search, moduleCode, period, onNavigate }) {
    // No warnings at all.
    if (structured.length === 0 && legacyStrings.length === 0) {
        return (
            <div className="asl-card p-4">
                <div className="text-sm text-[color:var(--asl-muted)]" data-testid="warn-empty">
                    {search && rawCount > 0
                        ? "No warnings match your search."
                        : "No warnings — clean run."}
                </div>
            </div>
        );
    }

    // Structured cards (Iter60.p49 Item A). Fall back to bullets only
    // if the backend didn't populate `warning_details` at all AND the
    // p49.1 legacy rebuild also failed to produce entries.
    if (hasStructured) {
        return (
            <div className="asl-card p-4">
                <ul className="space-y-2" data-testid="warn-list">
                    {structured.map((d, i) => (
                        <WarningCard
                            key={i}
                            detail={d}
                            moduleCode={moduleCode}
                            period={period}
                            onNavigate={onNavigate}
                        />
                    ))}
                </ul>
            </div>
        );
    }

    return (
        <div className="asl-card p-4">
            <ul className="space-y-1 text-xs" data-testid="warn-list">
                {legacyStrings.map((w, i) => <li key={i} className="text-amber-700">• {w}</li>)}
            </ul>
        </div>
    );
}

function WarningCard({ detail, moduleCode, period, onNavigate }) {
    // Iter60.p49.1 Gap 2 — accept both `type` (new spec) and `kind`
    // (original p49 shape) for backward-compat with older docs.
    const discriminator = detail.type || detail.kind || "legacy_unstructured";
    const meta = WARNING_KIND_META[discriminator] || WARNING_KIND_META.legacy_unstructured;
    const isNoRate = discriminator === "skipped_no_rate";
    const canFixWageRate = isNoRate && detail.fix_cta?.type === "add_wage_rate";
    const gotoWageRate = () => {
        const p = detail.fix_cta?.params || {};
        const qs = new URLSearchParams({
            prefill_employee: p.prefill_employee || "",
            prefill_job_title: p.prefill_job_title || "",
            prefill_worker_category: p.prefill_worker_category || "casual",
        }).toString();
        const code = (moduleCode || "").toLowerCase();
        if (code) {
            onNavigate(`/m/${code}/wage-rates?${qs}`);
        } else {
            onNavigate(`/wage-rates?${qs}`);
        }
    };
    const testKey = detail.module_employee_id || detail.employee_id || detail.employee_name || "unknown";
    return (
        <li
            className="border rounded-lg p-3 flex flex-wrap items-start justify-between gap-3"
            style={{ background: meta.bg, borderColor: meta.border }}
            data-testid={`warn-card-${testKey}`}
        >
            <div className="min-w-0 flex-1 space-y-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded"
                        style={{ background: meta.chipBg, color: meta.chipFg }}
                        data-testid={`warn-kind-${testKey}`}
                    >
                        {meta.label}
                    </span>
                    <span className="font-medium text-sm asl-navy-text" data-testid={`warn-name-${testKey}`}>
                        {detail.employee_name || "Unknown"}
                    </span>
                    <span className="mono text-xs text-[color:var(--asl-muted)]" data-testid={`warn-mid-${testKey}`}>
                        · Module ID {detail.module_employee_id || "—"}
                    </span>
                </div>
                <div className="text-xs text-[color:var(--asl-text-2)]" data-testid={`warn-reason-${testKey}`}>
                    Reason: {detail.reason}
                </div>
                {(detail.job_role || detail.worker_category) && (
                    <div className="text-[11px] text-[color:var(--asl-muted)]">
                        {detail.job_role && <>Job role: <span className="mono">{detail.job_role}</span></>}
                        {detail.job_role && detail.worker_category && <span className="mx-1">·</span>}
                        {detail.worker_category && <>Category: <span className="mono">{detail.worker_category}</span></>}
                    </div>
                )}
            </div>
            <div className="flex flex-col items-end gap-1">
                {canFixWageRate && (
                    <Button
                        size="sm"
                        className="asl-crimson-bg text-white"
                        onClick={gotoWageRate}
                        data-testid={`warn-fix-${testKey}`}
                    >
                        Fix — Add wage rate
                    </Button>
                )}
            </div>
        </li>
    );
}
